import { Tab } from '@headlessui/react';
import clsx from 'clsx';
import React from 'react';

interface Props {
  className?: string;
  children: React.ReactNode;
  defaultIndex?: number;
}

function Tabs({ className, children, defaultIndex }: Props): JSX.Element {
  return (
    <div className={clsx('', className)}>
      <Tab.Group defaultIndex={defaultIndex}>{children}</Tab.Group>
    </div>
  );
}

interface TabsListProps {
  className?: string;
  children: React.ReactNode;
}

Tabs.List = function TabsList({
  className,
  children,
}: TabsListProps): JSX.Element {
  return (
    <Tab.List as="div" className={clsx('border-b border-gray-200', className)}>
      <nav className="-mb-px flex space-x-8">{children}</nav>
    </Tab.List>
  );
};

interface TabsTabProps {
  className?: string;
  children: React.ReactNode;
  count?: number;
}

Tabs.Tab = function TabsTab({
  className,
  children,
  count,
}: TabsTabProps): JSX.Element {
  return (
    <Tab as={React.Fragment}>
      {({ selected }) => (
        <button
          type="button"
          className={clsx(
            'flex whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium',
            {
              'border-blue-500 text-blue-600': selected,
              'border-transparent text-gray-500 hover:border-gray-200 hover:text-gray-700':
                !selected,
            },
            className,
          )}
          aria-current={selected ? 'page' : undefined}
        >
          {children}
          {count ? (
            <span
              className={clsx(
                selected
                  ? 'bg-blue-100 text-blue-600'
                  : 'bg-gray-100 text-gray-900',
                'ml-3 hidden rounded-full px-2.5 py-0.5 text-xs font-medium md:inline-block',
              )}
            >
              {count}
            </span>
          ) : null}
        </button>
      )}
    </Tab>
  );
};

interface TabsPanelsProps {
  className?: string;
  children: React.ReactNode;
}

Tabs.Panels = function TabsPanels({
  className,
  children,
}: TabsPanelsProps): JSX.Element {
  return <Tab.Panels className={clsx('', className)}>{children}</Tab.Panels>;
};

interface TabsPanelProps {
  className?: string;
  children: React.ReactNode;
}

Tabs.Panel = function TabsPanel({
  className,
  children,
}: TabsPanelProps): JSX.Element {
  return <Tab.Panel className={clsx('', className)}>{children}</Tab.Panel>;
};

export default Tabs;
